"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

const COUNTRIES = [
  { code: "TT", name: "Trinidad & Tobago" },
  { code: "JM", name: "Jamaica" },
  { code: "BB", name: "Barbados" },
  { code: "GD", name: "Grenada" },
  { code: "LC", name: "St. Lucia" },
  { code: "VC", name: "St. Vincent" },
  { code: "DM", name: "Dominica" },
  { code: "AG", name: "Antigua & Barbuda" },
  { code: "KN", name: "St. Kitts & Nevis" },
  { code: "BS", name: "Bahamas" },
  { code: "GY", name: "Guyana" },
  { code: "SR", name: "Suriname" },
];

const CURRENCIES = ["TTD", "JMD", "BBD", "USD"];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function BrandSignupPage() {
  const router = useRouter();
  const [form, setForm] = useState({
    brandName: "",
    slug: "",
    contactName: "",
    email: "",
    password: "",
    country: "TT",
    currency: "TTD",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "brandName" ? { slug: slugify(value) } : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Signup failed");
        return;
      }

      router.push("/brand/dashboard");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="brand-auth">
      <div className="brand-auth__card brand-auth__card--wide">
        <div className="brand-auth__header">
          <h1 className="brand-auth__title">DressingRoom</h1>
          <p className="brand-auth__subtitle">Create Your Brand Account</p>
        </div>

        <form onSubmit={handleSubmit} className="brand-auth__form">
          {error && <div className="brand-auth__error">{error}</div>}

          <div className="brand-form-row">
            <div className="field-group">
              <label className="field-label">Brand Name</label>
              <input
                type="text"
                value={form.brandName}
                onChange={(e) => updateField("brandName", e.target.value)}
                required
                className="brand-input"
                placeholder="Acme Apparel"
              />
            </div>

            <div className="field-group">
              <label className="field-label">Store Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                required
                className="brand-input"
                placeholder="acme-apparel"
              />
            </div>
          </div>

          <div className="brand-form-row">
            <div className="field-group">
              <label className="field-label">Your Name</label>
              <input
                type="text"
                value={form.contactName}
                onChange={(e) => updateField("contactName", e.target.value)}
                required
                className="brand-input"
                placeholder="Jane Doe"
              />
            </div>

            <div className="field-group">
              <label className="field-label">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                required
                className="brand-input"
                placeholder="you@brand.com"
              />
            </div>
          </div>

          <div className="field-group">
            <label className="field-label">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => updateField("password", e.target.value)}
              required
              minLength={6}
              className="brand-input"
              placeholder="••••••••"
            />
          </div>

          <div className="brand-form-row">
            <div className="field-group">
              <label className="field-label">Country</label>
              <div className="select-box">
                <select
                  value={form.country}
                  onChange={(e) => updateField("country", e.target.value)}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">Currency</label>
              <div className="select-box">
                <select
                  value={form.currency}
                  onChange={(e) => updateField("currency", e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="button button--primary button--block"
            disabled={loading}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <p className="brand-auth__footer">
          Already have an account?{" "}
          <a href="/brand/login" className="brand-auth__link">
            Sign In
          </a>
        </p>
      </div>
    </div>
  );
}
